import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useIsMobile } from '../hooks/useResponsiveImage';

export const EASE: [number, number, number, number] = [0.16, 1, 0.3, 1];

/** Image that drifts vertically as its section scrolls (subtle parallax). */
export const ParallaxImg = ({
  src,
  srcMobile,
  alt,
  speed = 10,
  className = '',
  imgClassName = '',
  overlayClassName = '',
}: {
  src: string;
  srcMobile?: string;
  alt: string;
  speed?: number;
  className?: string;
  imgClassName?: string;
  /** Rendered as a sibling of the image inside the same clipped box, so a gradient overlay
   *  can never drift a subpixel out of alignment with the photo beneath it. */
  overlayClassName?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });
  const y = useTransform(scrollYProgress, [0, 1], [speed, -speed]);

  // Use the mobile variant below the breakpoint when one is provided.
  const isMobile = useIsMobile();
  const imageSrc = srcMobile && isMobile ? srcMobile : src;
  
  // Tailwind's `.relative` rule is defined after `.absolute` in its stylesheet, so if a
  // caller passes `absolute` in `className` it would silently lose to a hardcoded `relative`
  // here (same specificity, later rule wins) — collapsing this wrapper to 0 height. Only
  // default to `relative` when the caller isn't already establishing positioning itself.
  const needsRelative = !/\b(absolute|fixed|sticky)\b/.test(className);

  // Mobile Safari shows a hairline gap at the top/bottom of a transformed child inside an
  // `overflow-hidden` box (subpixel clipping bug) — most visible where a photo meets a flat
  // gradient. Parallax adds little on a small screen anyway, so skip the transform there.
  const style = isMobile ? undefined : { y };

  return (
    <div
      ref={ref}
      className={`${needsRelative ? 'relative ' : ''}overflow-hidden ${className}`}
      style={{ WebkitMaskImage: '-webkit-radial-gradient(white, black)' }}
    >
      <motion.img
        src={imageSrc}
        alt={alt}
        loading="lazy"
        style={style}
        className={`absolute -top-[8%] left-0 h-[116%] w-full object-cover will-change-transform ${imgClassName}`}
      />
      {overlayClassName && (
        <div className={`absolute -inset-px ${overlayClassName}`} />
      )}
    </div>
  );
};

/** One word revealed from behind a mask. */
export const RevealWord = ({
  children,
  delay = 0,
  className = '',
}: {
  children: React.ReactNode;
  delay?: number;
  className?: string;
}) => (
  <span className="inline-block overflow-hidden pb-2 align-top">
    <motion.span
      className={`inline-block ${className}`}
      initial={{ y: '110%', rotate: 3, opacity: 0 }}
      animate={{ y: 0, rotate: 0, opacity: 1 }}
      transition={{ duration: 0.9, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

/** Standard scroll-triggered fade/rise-in, spread onto a motion element. */
export const fadeUp = {
  initial: { opacity: 0, y: 36 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 1, ease: EASE },
};
